import React, { useState, useEffect, useRef } from 'react';
import { db, storage, auth } from './firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import TipTapEditor from './components/TipTapEditor';
import './EternalCupEventManager.css';

const EternalCupEventManager = () => {
  const [flyerUrl, setFlyerUrl] = useState('');
  const [infoContent, setInfoContent] = useState('');
  const [musicTracks, setMusicTracks] = useState([]);
  const [locationInfo, setLocationInfo] = useState('');
  const [mapEmbedUrl, setMapEmbedUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingFlyer, setUploadingFlyer] = useState(false);
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  const flyerInputRef = useRef(null);

  useEffect(() => {
    // Check authentication state
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setAuthChecked(true);
      if (user) {
        console.log('✅ User authenticated:', user.email);
        loadEventData();
      } else {
        console.error('❌ User not authenticated');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadEventData = async () => {
    try {
      const docRef = doc(db, 'eternalCupEvent', 'currentEvent');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        setFlyerUrl(data.flyerUrl || '');
        setInfoContent(data.infoContent || '');
        setMusicTracks(data.musicTracks || []);
        setLocationInfo(data.locationInfo || '');
        setMapEmbedUrl(data.mapEmbedUrl || '');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading event data:', error);
      alert('Σφάλμα φόρτωσης δεδομένων: ' + error.message);
      setLoading(false);
    }
  };

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Resize if image is larger than maxWidth
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              }));
            },
            'image/jpeg',
            quality
          );
        };
      };
    });
  };

  const handleFlyerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Παρακαλώ επιλέξτε αρχείο εικόνας');
      return;
    }

    // Validate file size (max 5MB for original file)
    if (file.size > 5 * 1024 * 1024) {
      alert('Το αρχείο είναι πολύ μεγάλο. Μέγιστο μέγεθος: 5MB');
      return;
    }

    setUploadingFlyer(true);

    try {
      // Check if user is authenticated
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Πρέπει να συνδεθείτε για να ανεβάσετε αρχεία');
      }
      
      console.log('🔐 Uploading as user:', user.email);
      console.log('📦 Original file size:', (file.size / 1024 / 1024).toFixed(2) + ' MB');
      
      // Compress the image before upload
      const compressedFile = await compressImage(file, 1200, 0.85);
      console.log('� Compressed file size:', (compressedFile.size / 1024 / 1024).toFixed(2) + ' MB');
      
      const storageRef = ref(storage, `eternal-cup-event/flyer_${Date.now()}.jpg`);
      await uploadBytes(storageRef, compressedFile);
      const url = await getDownloadURL(storageRef);
      setFlyerUrl(url);
      
      // Auto-save to Firestore
      const docRef = doc(db, 'eternalCupEvent', 'currentEvent');
      await setDoc(docRef, {
        flyerUrl: url,
        infoContent,
        musicTracks,
        locationInfo,
        mapEmbedUrl,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      alert('✅ Η αφίσα ανέβηκε και αποθηκεύτηκε επιτυχώς!');
    } catch (error) {
      console.error('Error uploading flyer:', error);
      if (error.code === 'storage/unauthorized') {
        alert('❌ Δεν έχετε δικαίωμα πρόσβασης. Παρακαλώ συνδεθείτε ως admin.');
      } else {
        alert('Σφάλμα κατά την μεταφόρτωση: ' + error.message);
      }
    } finally {
      setUploadingFlyer(false);
    }
  };

  const handleMusicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('audio/')) {
      alert('Παρακαλώ επιλέξτε αρχείο ήχου');
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      alert('Το αρχείο είναι πολύ μεγάλο. Μέγιστο μέγεθος: 20MB');
      return;
    }

    const title = prompt('Δώστε τίτλο για αυτό το κομμάτι:');
    if (!title) return;

    setUploadingMusic(true);

    try {
      // Check if user is authenticated
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Πρέπει να συνδεθείτε για να ανεβάσετε αρχεία');
      }
      
      // Get the auth token to verify it's valid
      const token = await user.getIdToken();
      console.log('🔐 Uploading music as user:', user.email);
      console.log('🔑 Token preview:', token.substring(0, 50) + '...');
      
      const storageRef = ref(storage, `eternal-cup-event/music/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      
      const newTrack = { title, url, uploadedAt: new Date().toISOString() };
      const updatedTracks = [...musicTracks, newTrack];
      setMusicTracks(updatedTracks);
      
      // Auto-save to Firestore
      const docRef = doc(db, 'eternalCupEvent', 'currentEvent');
      await setDoc(docRef, {
        flyerUrl,
        infoContent,
        musicTracks: updatedTracks,
        locationInfo,
        mapEmbedUrl,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      alert('✅ Το μουσικό κομμάτι ανέβηκε και αποθηκεύτηκε επιτυχώς!');
    } catch (error) {
      console.error('Error uploading music:', error);
      if (error.code === 'storage/unauthorized') {
        alert('❌ Δεν έχετε δικαίωμα πρόσβασης. Παρακαλώ συνδεθείτε ως admin.');
      } else {
        alert('Σφάλμα κατά την μεταφόρτωση: ' + error.message);
      }
    } finally {
      setUploadingMusic(false);
    }
  };

  const handleDeleteMusic = async (index) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το κομμάτι;')) {
      return;
    }

    try {
      const newTracks = [...musicTracks];
      newTracks.splice(index, 1);
      setMusicTracks(newTracks);
      
      // Auto-save to Firestore
      const docRef = doc(db, 'eternalCupEvent', 'currentEvent');
      await setDoc(docRef, {
        flyerUrl,
        infoContent,
        musicTracks: newTracks,
        locationInfo,
        mapEmbedUrl,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      
      alert('✅ Το κομμάτι διαγράφηκε επιτυχώς!');
    } catch (error) {
      console.error('Error deleting music:', error);
      alert('Σφάλμα κατά τη διαγραφή: ' + error.message);
    }
  };

  const handleSave = async () => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να αποθηκεύσετε τις αλλαγές;')) {
      return;
    }

    setSaving(true);

    try {
      const docRef = doc(db, 'eternalCupEvent', 'currentEvent');
      await setDoc(docRef, {
        flyerUrl,
        infoContent,
        musicTracks,
        locationInfo,
        mapEmbedUrl,
        updatedAt: new Date().toISOString()
      });

      alert('✅ Οι αλλαγές αποθηκεύτηκαν επιτυχώς!');
    } catch (error) {
      console.error('Error saving event data:', error);
      alert('Σφάλμα αποθήκευσης: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!authChecked || loading) {
    return (
      <div className="eternal-cup-manager-container">
        <div className="manager-loading">
          <div className="loading-spinner"></div>
          <p>{!authChecked ? 'Έλεγχος ταυτοποίησης...' : 'Φόρτωση...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="eternal-cup-manager-container">
      <div className="manager-header">
        <h1>🏆 Διαχείριση Eternal Cup Event</h1>
        <p className="manager-subtitle">Ενημερώστε το περιεχόμενο της σελίδας εκδήλωσης</p>
      </div>

      {/* Flyer Section */}
      <div className="manager-section">
        <div className="section-header">
          <h2>📋 Αφίσα Εκδήλωσης</h2>
        </div>
        <div className="flyer-upload-section">
          {flyerUrl && (
            <div className="flyer-preview">
              <img src={flyerUrl} alt="Event Flyer Preview" />
            </div>
          )}
          <input
            ref={flyerInputRef}
            type="file"
            accept="image/*"
            onChange={handleFlyerUpload}
            style={{ display: 'none' }}
          />
          <button
            className="upload-button"
            onClick={() => flyerInputRef.current.click()}
            disabled={uploadingFlyer}
          >
            {uploadingFlyer ? '⏳ Μεταφόρτωση...' : '📤 Ανέβασμα Αφίσας'}
          </button>
          <p className="upload-hint">Υποστηριζόμενες μορφές: JPG, PNG, WEBP (Max: 5MB)</p>
        </div>
      </div>

      {/* Info Content Section */}
      <div className="manager-section">
        <div className="section-header">
          <h2>📝 Πληροφορίες Διαγωνισμού</h2>
          <p className="section-subtitle">Γράψτε πληροφορίες για το event (κανόνες, κατηγορίες, κ.λπ.)</p>
        </div>
        <div className="editor-container">
          <TipTapEditor
            value={infoContent}
            onChange={setInfoContent}
            placeholder="Γράψτε τις πληροφορίες του διαγωνισμού εδώ..."
          />
        </div>
      </div>

      {/* Music Section */}
      <div className="manager-section">
        <div className="section-header">
          <h2>🎵 Μουσική Solo</h2>
          <p className="section-subtitle">Προσθέστε μουσικά κομμάτια για τις solo κατηγορίες</p>
        </div>
        <div className="music-upload-section">
          <input
            type="file"
            accept="audio/*"
            onChange={handleMusicUpload}
            style={{ display: 'none' }}
            id="music-upload-input"
          />
          <button
            className="upload-button"
            onClick={() => document.getElementById('music-upload-input').click()}
            disabled={uploadingMusic}
          >
            {uploadingMusic ? '⏳ Μεταφόρτωση...' : '🎼 Προσθήκη Μουσικού Κομματιού'}
          </button>
          <p className="upload-hint">Υποστηριζόμενες μορφές: MP3, WAV, OGG (Max: 20MB)</p>

          {musicTracks.length > 0 && (
            <div className="music-list">
              <h3>Μουσικά Κομμάτια ({musicTracks.length})</h3>
              {musicTracks.map((track, index) => (
                <div key={index} className="music-item">
                  <div className="music-item-info">
                    <span className="music-item-icon">🎼</span>
                    <span className="music-item-title">{track.title}</span>
                  </div>
                  <button
                    className="delete-music-button"
                    onClick={() => handleDeleteMusic(index)}
                  >
                    🗑️ Διαγραφή
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Location Section */}
      <div className="manager-section">
        <div className="section-header">
          <h2>📍 Τοποθεσία</h2>
          <p className="section-subtitle">Προσθέστε πληροφορίες τοποθεσίας και χάρτη</p>
        </div>
        
        <div className="editor-container">
          <label className="input-label">Κείμενο Τοποθεσίας:</label>
          <TipTapEditor
            value={locationInfo}
            onChange={setLocationInfo}
            placeholder="Γράψτε πληροφορίες για την τοποθεσία (διεύθυνση, οδηγίες, κ.λπ.)..."
          />
        </div>

        <div className="map-embed-section">
          <label className="input-label">Google Maps Embed URL:</label>
          <input
            type="text"
            className="map-input"
            value={mapEmbedUrl}
            onChange={(e) => setMapEmbedUrl(e.target.value)}
            placeholder="https://www.google.com/maps/embed?pb=..."
          />
          <p className="input-hint">
            💡 Πηγαίνετε στο Google Maps → Κάντε κλικ στο "Share" → "Embed a map" → Αντιγράψτε το URL
          </p>
          {mapEmbedUrl && (
            <div className="map-preview">
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="300"
                style={{ border: 0, borderRadius: '10px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Map Preview"
              ></iframe>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="manager-actions">
        <button
          className="save-button"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '⏳ Αποθήκευση...' : '💾 Αποθήκευση Αλλαγών'}
        </button>
        <a
          href="/eternal-cup"
          target="_blank"
          rel="noopener noreferrer"
          className="preview-button"
        >
          👁️ Προεπισκόπηση Σελίδας
        </a>
      </div>
    </div>
  );
};

export default EternalCupEventManager;
